import { useIsLocalMode } from '../lib/offline/state';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { Button, ConfirmDialog, Sheet, SheetContent, SheetTitle } from '@mlm/ui';
import type { PosProduct } from '../lib/api';
import { useHasPerm } from '../lib/auth-store';
import { useSaleStore, type SaleMode } from '../lib/cart-store';
import { useMediaQuery } from '../lib/use-media-query';
import { usePosSale } from '../lib/use-pos-sale';
import { BranchPicker } from '../components/branch-picker';
import { CartPanel } from '../components/pos/cart-panel';
import { Catalog } from '../components/pos/catalog';
import { CustomerSheet } from '../components/pos/customer-sheet';
import { FreeSlotDialog } from '../components/pos/free-slot-dialog';
import { ItemSheet } from '../components/pos/item-sheet';
import { SlotPickerDialog } from '../components/pos/slot-picker-dialog';

/**
 * The till. `mode` is the tab: an ordinary sale, or "Sell to Stockist".
 *
 * The sale itself lives in a store shared with the checkout page
 * (`lib/cart-store.ts`), so the tab on screen has to be told to it first.
 * Nothing renders until the two agree, which is what guarantees a cart built
 * on one tab is never shown, or charged, on the other.
 */
export function PosPage({ mode = 'retail' }: { mode?: SaleMode } = {}) {
  const storeMode = useSaleStore((s) => s.mode);
  const setMode = useSaleStore((s) => s.setMode);
  useEffect(() => setMode(mode), [mode, setMode]);
  if (storeMode !== mode) return null;
  return <PosScreen />;
}

/**
 * Two panes: the catalog on the left, the cart on the right. Choosing the
 * customer and paying are steps of their own (the customer sheet, then the
 * `/checkout` page), so this screen stays products and a list.
 */
function PosScreen() {
  const navigate = useNavigate();
  const sale = usePosSale();
  const { stockistMode, branchId, cart, priceMap, addToCart, needsSlotPick, dismissReceipt } = sale;
  // One-shot confirmation on entering the Sell to Stockist tab — a transfer is
  // easy to mistake for a sale, so spell out the consequences up front. Shown
  // every time the tab is opened (the repo has no "don't show again" pattern).
  //
  // NOT shown when the cashier is coming BACK into a transfer already under
  // way: from the checkout or the receipt (they navigate with `resume`), or
  // with a cart or a stockist already picked. This screen remounts on every
  // such return, and the dialog's way out ("Go to Checkout", Escape, a tap
  // outside) leaves for the retail tab, which empties the sale.
  const location = useLocation();
  const resuming =
    (location.state as { resume?: boolean } | null)?.resume === true ||
    cart.length > 0 ||
    !!sale.member;
  const [stockistAck, setStockistAck] = useState(!stockistMode || resuming);
  // FS Activation is its own authority (`pos.slot.activate`) — the API has
  // always enforced it, but the button used to render for everyone, so an
  // unauthorised cashier only found out via a 403 mid-dialog.
  // Creating a slot needs the whole tree, so it waits for internet.
  const local = useIsLocalMode();
  const canActivateFs = useHasPerm('pos.slot.activate') && !local;

  const [showCustomer, setShowCustomer] = useState(false);
  const [showSlotPicker, setShowSlotPicker] = useState(false);
  // FS Activation — a self-contained "Activate Free Slot" flow, independent of
  // the cart/member selected for a sale.
  const [showFreeSlot, setShowFreeSlot] = useState(false);
  // Variation product awaiting a variation + quantity (opens the item sheet).
  const [sheetProduct, setSheetProduct] = useState<PosProduct | null>(null);

  // Back at the till means a new sale: the last receipt has been dealt with.
  useEffect(() => dismissReceipt(), [dismissReceipt]);
  // A member with several slots has to say which one is buying. Keyed on the
  // member too: picking a second multi-slot member while the first one's
  // picker was dismissed leaves `needsSlotPick` true throughout, and the
  // picker must still open for the new one.
  const memberId = sale.member?.id;
  useEffect(() => {
    if (needsSlotPick) setShowSlotPicker(true);
  }, [needsSlotPick, memberId]);

  // Photos in the cart only when the catalog has any (see the catalog's tiles).
  const showPhotos = useMemo(
    () => (sale.products ?? []).some((p) => p.images.length > 0),
    [sale.products],
  );

  // The cart is a fixed column from 1024 px up (the `lg` breakpoint) and a
  // slide-over below it.
  const wide = useMediaQuery('(min-width: 1024px)');
  const [showCart, setShowCart] = useState(false);
  const units = cart.reduce((n, l) => n + l.qty, 0);
  const cartPanel = (variant: 'pane' | 'sheet') => (
    <CartPanel
      sale={sale}
      showPhotos={showPhotos}
      variant={variant}
      onSelectCustomer={() => setShowCustomer(true)}
      onSwitchSlot={() => setShowSlotPicker(true)}
      // A member who owns several slots must choose one first. Going on
      // without would ring up a slotless sale for someone who has slots.
      onContinue={() => (needsSlotPick ? setShowSlotPicker(true) : navigate('/checkout'))}
    />
  );

  /** Tile tap: single-SKU adds directly; a variation product opens the sheet. */
  function onProductTap(p: PosProduct) {
    if (p.variants.length > 0) setSheetProduct(p);
    else addToCart(p);
  }

  if (!branchId) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Point of Sale</h1>
        <p className="mt-2 text-base text-muted-foreground">
          {sale.branchesLoading ? 'Loading…' : 'No branches available.'}
        </p>
      </div>
    );
  }

  return (
    <div className={wide ? 'flex items-start' : 'flex min-h-screen flex-col'}>
      <div className="min-w-0 flex-1 space-y-4 p-4 md:p-6">
        {stockistMode && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Ring up items for a stockist. On checkout the stock moves out of this branch and into
            the stockist&apos;s own branch inventory.
          </div>
        )}
        <Catalog
          title={stockistMode ? 'Sell to Stockist' : 'Point of Sale'}
          actions={
            <>
              {canActivateFs && (
                <Button variant="outline" className="h-12" onClick={() => setShowFreeSlot(true)}>
                  Activate free slot
                </Button>
              )}
              <BranchPicker
                branches={sale.branches}
                branchId={branchId}
                isScoped={!!sale.user?.branchId}
                onChange={sale.selectBranch}
              />
            </>
          }
          products={sale.products}
          loading={sale.productsLoading}
          priceMap={priceMap}
          onTap={onProductTap}
          onScan={addToCart}
        />
      </div>

      {wide ? (
        cartPanel('pane')
      ) : (
        // Narrow screens (a tablet held upright, a phone): the cart would sit
        // far below a long catalog, so it becomes a slide-over opened from a
        // bar that stays at the foot of the screen.
        <>
          <div className="sticky bottom-0 z-10 flex items-center gap-3 border-t bg-card px-4 py-3 shadow-elevated">
            <ShoppingCart className="h-6 w-6 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="text-xl font-semibold tabular-nums text-primary">
                ₱{sale.goodsTotal.toFixed(2)}
              </div>
              <div className="truncate text-sm text-muted-foreground">
                {units} {units === 1 ? 'unit' : 'units'}
                {sale.member ? ` · ${sale.member.fullName ?? sale.member.email}` : ''}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowCart(true)}
              className="h-12 rounded-[10px] bg-primary px-6 text-base font-semibold text-primary-foreground"
            >
              View cart
            </button>
          </div>
          <Sheet open={showCart} onOpenChange={setShowCart}>
            <SheetContent side="right" className="w-full max-w-full p-0 sm:max-w-[420px]">
              <SheetTitle className="sr-only">Cart</SheetTitle>
              {cartPanel('sheet')}
            </SheetContent>
          </Sheet>
        </>
      )}

      <CustomerSheet
        open={showCustomer}
        stockistMode={stockistMode}
        onPick={(m) => {
          sale.pickMember(m);
          setShowCustomer(false);
        }}
        onClose={() => setShowCustomer(false)}
      />

      <SlotPickerDialog
        open={showSlotPicker}
        slots={sale.slots}
        memberLabel={sale.member?.fullName ?? sale.member?.email ?? ''}
        showGc={sale.offersGc}
        showCoin={sale.offersCoin}
        onPick={(s) => {
          sale.setSlot(s.id, s.slotCode);
          setShowSlotPicker(false);
        }}
        onClose={() => setShowSlotPicker(false)}
      />

      <ItemSheet
        product={sheetProduct}
        cart={cart}
        priceMap={priceMap}
        onAdd={addToCart}
        onClose={() => setSheetProduct(null)}
      />

      <FreeSlotDialog open={showFreeSlot && canActivateFs} onClose={() => setShowFreeSlot(false)} />

      <ConfirmDialog
        open={stockistMode && !stockistAck}
        onOpenChange={(o) => {
          if (!o) navigate('/');
        }}
        title="Sell to Stockist — this is a transfer, not a sale"
        confirmLabel="I understand — continue"
        cancelLabel="Go to Checkout"
        description={
          <>
            On checkout the stock moves out of this branch and into the stockist&apos;s own branch
            inventory. This is a <strong>restock</strong>: no PV is earned, no commissions are paid
            to anyone, and no Code+PIN is issued. The stockist pays for the goods now — the PV and
            commissions happen later, when they sell these items onward. To ring up an ordinary
            PV-earning sale, use the <strong>Checkout</strong> tab instead.
          </>
        }
        onConfirm={() => setStockistAck(true)}
      />
    </div>
  );
}
